import { Component, EventEmitter, Input, Output, SecurityContext } from '@angular/core';
import { CountryDocListItem, CountryInfo } from '@secca/shared/models/SupplierSearchResponse';
import { CountryDetails } from '@secca/shared/models/country';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-country-info',
    templateUrl: './country-info.component.html',
    styleUrls: ['./country-info.component.scss'],
    standalone: false
})
export class CountryInfoComponent {
  @Input() countryBasics: CountryDetails;
  @Input() countryInfo: CountryInfo;
  @Output() closeCountryInfo = new EventEmitter<string>();

  dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  constructor(private sanitizer: DomSanitizer) {
  }

  private cleanLinebreaksOnlyHtml(input: string): string {
    // Remove all tags except <br />
    let output = input.replace(/<\/?[^>]+(>|$)/g, (match) =>
      ['<br>', '<br/>', '<br />'].includes(match.trim().toLowerCase()) ? '<br />' : ''
    );
    // reduce triple+ linebreaks
    output = output.replace(/(\s*<br \/>){3,}/g, '<br /><br />');
    return output;
  }

  /**
   * Converts linebreak-characters to HTML <br /> tags.
   */
  private linebreaksToHtml(input: string): string {
    return input.replace(/(\r\n|\n|\r)/g, '<br />');
  }

  /**
   * Converts string with linebreak-characters to frontend ready html formatted (only) with safe linebreak-tags
   */
  private linebreaksToCleanHtml(input: string): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.cleanLinebreaksOnlyHtml(this.linebreaksToHtml(input)));
  }

  public formatDate(ts: string): string {
    if (ts) {
      return new Date(ts).toLocaleDateString(undefined, this.dateOptions);
    }
    return '';
  }

  public scrollToElement(selector: string) {
    setTimeout(() => {
      const shownCountryInfo = document.querySelector('mat-dialog-container:not(.mat-dialog-hide) app-country-info');
      const element = shownCountryInfo?.querySelector(selector);
      if (!!element) {
        element.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
      }
    });
  }

  get importantInformationAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.importantInformation ?? '');
  }

  get handlingOfMortalRemainsAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.handlingOfMortalRemains ?? '');
  }

  get emergencyServiceAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.emergencyService ?? '');
  }

  get evacuationRepatriationAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.evacuationRepatriation ?? '');
  }

  get acProcessesAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.acProcesses ?? '');
  }

  get publicHealthCareAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.publicHealthCare ?? '');
  }

  get privateHealthCareAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.privateHealthCare ?? '');
  }

  get ehicCopaymentRemarkAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.ehicCopaymentRemark ?? '');
  }

  get reciprocalHealthCareTreatyAgreementRemarkAsHtml(): string {
    return this.linebreaksToCleanHtml(this.countryInfo?.reciprocalHealthCareTreatyAgreementRemark ?? '');
  }

  get countryDocuments(): CountryDocListItem[] {
    return this.countryInfo?.doc_list || [];
  }

  close() {
    this.closeCountryInfo.emit('close');
  }
}
